import moment from 'moment'
import {
    FC,
    PropsWithChildren,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react'
import { createContext, useContextSelector } from 'use-context-selector'
import { getAllTreatments } from '../../services/dashboard'
import { MESSAGES } from '../../utils/constants'
import { TreatmentReportResponse } from '../../utils/types/dashboard'
import { UseTreatmentContext } from '../../utils/types/common'

type TreatmentContextValues = {
    treatments: Map<number, TreatmentReportResponse>
    loading: boolean
    error?: string
}

const TreatmentContext = createContext<TreatmentContextValues | null>(null)

const useTreatmentHistory = () => {
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string>()

    const [state, setState] = useState<Map<number, TreatmentReportResponse>>(
        new Map()
    )

    const getTreatments = useCallback(async () => {
        try {
            setLoading(true)
            const response = await getAllTreatments(
                moment().subtract(365, 'days').unix(),
                moment().unix()
            )
            if (response) {
                setState(new Map(response.map((entry) => [entry.date, entry])))
            }
        } catch (error) {
            setError(MESSAGES.error.endpointError)
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        getTreatments().catch((error) => console.log(error))
    }, [getTreatments])

    return [state, loading, error] as const
}

export const TreatmentProvider: FC<PropsWithChildren<unknown>> = ({
    children,
}) => {
    const [treatments, loading, error] = useTreatmentHistory()

    return (
        <TreatmentContext.Provider
            value={useMemo(
                () => ({ treatments, loading, error }),
                [treatments, loading, error]
            )}
        >
            {children}
        </TreatmentContext.Provider>
    )
}

export const useTreatmentContext: UseTreatmentContext<
    TreatmentContextValues
> = (contextFn) => {
    return useContextSelector(TreatmentContext, (state) => {
        if (!state) {
            throw new Error('Can only use inside Treatment Provider')
        }
        return contextFn(state)
    })
}
